import type {
  BeverageInspectionQuestionnaireListItemDto,
  BeverageQuestionOptionDto,
  BeverageQuestionTreeDto,
  CurrentBeverageInspectionQuestionnaireResponseDto,
  QuestionnaireSectionDto,
} from '../types/api';
import { compareSectionOrder } from './complianceQuestionnaireNormalize';
import { extractNumericOrder, normalizeStringId, pickArray, unwrapToDataObject } from './questionnaireShared';

/**
 * Beverage inspection questionnaires are a brand-new engine (no legacy flat catalog to merge,
 * unlike compliance), so this file is deliberately smaller than complianceQuestionnaireNormalize.ts —
 * just tree normalization, visibility, and ordering.
 */

function normalizeBeverageOption(opt: BeverageQuestionOptionDto): BeverageQuestionOptionDto {
  const raw = opt as unknown as Record<string, unknown>;
  const id = normalizeStringId(opt.id ?? raw.id ?? raw.option_id ?? raw.optionId);
  return { ...opt, id: id ?? opt.id };
}

export function normalizeBeverageQuestionNode(q: BeverageQuestionTreeDto): BeverageQuestionTreeDto {
  const raw = q as unknown as Record<string, unknown>;

  const sectionId = normalizeStringId(q.sectionId ?? raw.section_id);
  const parentQuestionId = normalizeStringId(q.parentQuestionId ?? raw.parent_question_id);
  const triggerOptionId = normalizeStringId(q.triggerOptionId ?? raw.trigger_option_id);
  const skipIfOptionId = normalizeStringId(q.skipIfOptionId ?? raw.skip_if_option_id);

  const order = extractNumericOrder(
    [q.order, raw.order, raw.display_order, raw.displayOrder, raw.sort_order, raw.sortOrder],
    q.order ?? 0
  );

  const subQuestions = Array.isArray(q.subQuestions)
    ? q.subQuestions.map(normalizeBeverageQuestionNode)
    : Array.isArray(raw.sub_questions)
      ? (raw.sub_questions as BeverageQuestionTreeDto[]).map(normalizeBeverageQuestionNode)
      : [];

  const options = (Array.isArray(q.options) ? q.options : (raw.options as BeverageQuestionOptionDto[]) ?? []).map(
    normalizeBeverageOption
  );

  return {
    ...q,
    sectionId: sectionId ?? q.sectionId ?? null,
    parentQuestionId,
    triggerOptionId,
    skipIfOptionId,
    subQuestions,
    options,
    order,
  };
}

export function normalizeBeverageQuestionTree(nodes: BeverageQuestionTreeDto[]): BeverageQuestionTreeDto[] {
  if (!nodes?.length) return [];
  return nodes.map(normalizeBeverageQuestionNode);
}

/**
 * Skip (hide-when-selected) is the only conditional-visibility rule beverage questions use —
 * no show-if, unlike compliance.
 */
export function isBeverageQuestionTreeVisible(
  question: BeverageQuestionTreeDto,
  selectedOptionIds: Set<string>
): boolean {
  const skipId = question.skipIfOptionId;
  if (skipId) return !selectedOptionIds.has(String(skipId));
  return true;
}

/** All question IDs reachable from roots (including nested subQuestions). */
export function collectBeverageQuestionTreeIds(nodes: BeverageQuestionTreeDto[]): Set<string> {
  const ids = new Set<string>();
  const walk = (q: BeverageQuestionTreeDto) => {
    ids.add(q.id);
    (q.subQuestions ?? []).forEach(walk);
  };
  (nodes ?? []).forEach(walk);
  return ids;
}

/** Sort root questions by `order`, then id (deterministic). */
export function compareBeverageQuestionOrder(a: BeverageQuestionTreeDto, b: BeverageQuestionTreeDto): number {
  const oa = extractNumericOrder([a.order], 0);
  const ob = extractNumericOrder([b.order], 0);
  if (oa !== ob) return oa - ob;
  return a.id.localeCompare(b.id);
}

/** Admin list / assessor form: section order first, then question order within section (unsectioned last). */
export function sortBeverageQuestionnaireRoots(
  roots: BeverageQuestionTreeDto[],
  sections: QuestionnaireSectionDto[]
): BeverageQuestionTreeDto[] {
  const orderedSections = [...sections].sort(compareSectionOrder);
  const sectionRank = new Map<string, number>();
  orderedSections.forEach((s, i) => sectionRank.set(s.id, i));
  const unsectionedRank = Number.MAX_SAFE_INTEGER;

  const rankOf = (q: BeverageQuestionTreeDto) =>
    q.sectionId && sectionRank.has(q.sectionId) ? sectionRank.get(q.sectionId)! : unsectionedRank;

  return [...roots].sort((a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    if (ra !== rb) return ra - rb;
    return compareBeverageQuestionOrder(a, b);
  });
}

/** Group root (non-sub) questions by section id — `null` key holds unsectioned questions. */
export function groupBeverageQuestionsBySection(
  roots: BeverageQuestionTreeDto[]
): Map<string | null, BeverageQuestionTreeDto[]> {
  const groups = new Map<string | null, BeverageQuestionTreeDto[]>();
  for (const q of roots) {
    const key = q.sectionId ?? null;
    const list = groups.get(key) ?? [];
    list.push(q);
    groups.set(key, list);
  }
  return groups;
}

/**
 * Normalizes different backend shapes for GET .../beverage-questionnaires/phase/:phase/current
 * (envelope nesting, alternate keys) — same defensive approach as its compliance counterpart,
 * minus the legacy flat-catalog merge (beverage inspections have no such legacy catalog).
 */
export function parseCurrentBeverageInspectionQuestionnaireResponse(
  payload: unknown
): CurrentBeverageInspectionQuestionnaireResponseDto {
  const empty: CurrentBeverageInspectionQuestionnaireResponseDto = {
    questionnaire: null,
    sections: [],
    questions: [],
  };
  if (payload === null || payload === undefined) return empty;

  const root = unwrapToDataObject(payload);

  const questionnaire =
    (root.questionnaire as BeverageInspectionQuestionnaireListItemDto | null | undefined) ?? null;

  const questions = pickArray(root, ['questions']) as BeverageQuestionTreeDto[];
  const sections = pickArray(root, ['sections']) as QuestionnaireSectionDto[];

  return { questionnaire, sections, questions };
}
